import React, {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  HTMLAttributes,
  ReactNode,
  Ref,
} from 'react';

import classNames from 'classnames';
import { Tooltip } from '@mui/material';

import classes from './SidebarItem.module.scss';

type SidebarItemBaseProps = {
  /** `menu`: small row with even 8px padding, for items inside a popup menu */
  size?: 'small' | 'medium' | 'menu';
  startIcon?: ReactNode;
  label: string;
  endIcon?: ReactNode;
  active?: boolean;
  activeHighlight?: boolean;
  /** Icon-only mode with label tooltip; caller passes sidebar collapsed state */
  collapsed?: boolean;
  badgeValue?: number | null;
  className?: string;
};

type AsSidebarLink = SidebarItemBaseProps & {
  as: 'a';
  ref?: Ref<HTMLAnchorElement>;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof SidebarItemBaseProps>;

type AsSidebarButton = SidebarItemBaseProps & {
  as: 'button';
  ref?: Ref<HTMLButtonElement>;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof SidebarItemBaseProps>;

export type SidebarItemProps = AsSidebarLink | AsSidebarButton;

const SidebarItem = ({
  as: Component,
  size = 'small',
  startIcon,
  label,
  endIcon,
  active,
  activeHighlight,
  collapsed: isCollapsed = false,
  badgeValue,
  className,
  ...props
}: SidebarItemProps) => {
  return (
    <Tooltip title={isCollapsed ? label : null} placement="right">
      <Component
        className={classNames(
          classes.item,
          classes[size],
          {
            [classes.active]: active,
            [classes.activeHighlight]: activeHighlight,
            [classes.collapsed]: isCollapsed,
          },
          className,
        )}
        {...(props as HTMLAttributes<HTMLElement>)}
      >
        {startIcon && <span className={classes.startIcon}>{startIcon}</span>}
        {!isCollapsed && <span className={classes.label}>{label}</span>}
        {!isCollapsed && endIcon && <span className={classes.endIcon}>{endIcon}</span>}
        {!isCollapsed && !!badgeValue && (
          <span className={classes.badgeValue}>{badgeValue}</span>
        )}
      </Component>
    </Tooltip>
  );
};

export { SidebarItem };
